import { useEffect, useRef, useState } from 'react';
import type { WebTask } from './tasks';

/** How long a resolved row stays in place, wearing its farewell, before it starts leaving. */
export const RESOLVE_HOLD_MS = 2000;
/** The leaving animation itself. Must match `.resolvedExit`'s duration in tasks.module.css. */
export const RESOLVE_EXIT_MS = 520;

/** How long a DISSOLVING row (a decision answered, not a task finished) stays in place before leaving.
 *  Shorter than the completion hold — there is no confetti to let land, and the row's own
 *  `TaskDecisionPromptRow` has already been showing its "picking this up" sentence since the click. */
export const DISSOLVE_HOLD_MS = 900;
/** Must match `.dissolveExit`'s duration in tasks.module.css. */
export const DISSOLVE_EXIT_MS = 420;

/** What a held row is doing right now: standing in its farewell, or on its way out. */
export type ResolvePhase = 'hold' | 'exit';

/** Why the row is leaving. A `complete` row finished — it wears the green sweep and cues the confetti
 *  burst. A `dissolve` row left because its open QUESTION got answered (any chip, including a decline
 *  like "Drop it") while the task's status never became COMPLETED — the work is not done, only the
 *  question is, so it gets a plain fade rather than a celebration. See `isDissolveTransition`. */
export type ResolveKind = 'complete' | 'dissolve';

/** A task caught mid-farewell: the row as it looked BEFORE it left, and what it is doing now. */
export interface HeldResolve {
  /** The pre-transition snapshot. Rendered in place of the live row — see `useResolvedTaskFarewell`. */
  task: WebTask;
  phase: ResolvePhase;
  kind: ResolveKind;
}

/**
 * The rule, kept pure so it is testable without a DOM (this package runs `testEnvironment: node`).
 *
 * A completion is worth marking only as a CHANGE seen by someone already watching. `before` `undefined`
 * means the task was first seen in whatever state it is now — a reload, a filter change, the first paint —
 * and has no transition to mark.
 */
export function isResolveTransition(before: string | undefined, after: string | undefined): boolean {
  return !!before && before !== 'COMPLETED' && after === 'COMPLETED';
}

/**
 * A DISSOLVE: the row was in `waiting` only because it held an open decision (see `awaitsDecision`), and
 * now it doesn't — while the task's status did not just become COMPLETED. Answering a question is never
 * completion (the backend never writes a status on the release, and the agent's follow-through is async
 * and unbounded — see `agent-task-buckets.ts`), so this is a distinct, quieter transition from
 * {@link isResolveTransition}: no green sweep, no confetti, a plain fade.
 *
 * `before` undefined means no transition to mark, same rule as `isResolveTransition`. Completion is
 * checked by the caller and takes precedence — a task that both answers its last question AND completes
 * in the same tick gets the celebration, not the dissolve.
 */
export function isDissolveTransition(before: Pick<WebTask, 'decisionPrompts'> | undefined, after: Pick<WebTask, 'decisionPrompts'>): boolean {
  if (!before) return false;
  const wasWaitingOnDecision = (before.decisionPrompts?.length ?? 0) > 0;
  const stillWaitingOnDecision = (after.decisionPrompts?.length ?? 0) > 0;
  return wasWaitingOnDecision && !stillWaitingOnDecision;
}

/**
 * Substitute the pre-completion snapshot for every task still saying goodbye.
 *
 * This is the whole trick, and it is why nothing downstream needs to know about the farewell. A completing
 * task changes bucket, and the list renders ONE bucket, so the row is dropped from the rendered set the
 * instant it finishes — it "vanished immediately, with no animation". The obvious repair, appending the
 * finished row back onto the bucket, is worse: it teleports a mid-list row to the bottom before playing
 * its farewell there, empties a single-task bucket (which then renders "No tasks yet" under the confetti),
 * and lets two siblings fold into a phantom origin group.
 *
 * Holding the OLD status instead means the row never leaves its bucket, its position, or its group in the
 * first place. Bucketing, ordering, the empty-state gate and the grouping threshold all keep working
 * unchanged, and the row animates exactly where the reader is already looking.
 */
export function withFarewellSnapshots(tasks: WebTask[], held: HeldResolve[]): WebTask[] {
  if (held.length === 0) {
    return tasks;
  }
  const snapshots = new Map(held.map((entry) => [entry.task.id, entry.task]));
  return tasks.map((task) => (task.id && snapshots.has(task.id) ? snapshots.get(task.id)! : task));
}

/** One step of the farewell machine, pure so the whole lifecycle is testable without a DOM or a clock. */
export function advanceFarewell(held: HeldResolve[], id: string, to: ResolvePhase | 'drop'): HeldResolve[] {
  if (to === 'drop') {
    return held.filter((entry) => entry.task.id !== id);
  }
  return held.map((entry) => (entry.task.id === id ? { ...entry, phase: to } : entry));
}

/**
 * The resolve farewell: which tasks just finished, and what their row should be doing about it.
 *
 * Owned by the LIST, not by the row: the row is dropped from the rendered set the moment its task
 * completes, so a per-row hook could observe the transition exactly never.
 *
 * Timers are keyed per task in a ref, NEVER driven by an effect on the held array. An effect that depends
 * on `held` and also writes to it re-runs on its own writes, and its cleanup then clears the pending
 * timers and re-arms them at a full hold — so one row resolving while another is mid-hold pushes the
 * first row's departure back, and a steady trickle leaves a growing stack of finished rows on screen for
 * ten seconds or more, static and green long after their 2s animation has ended.
 *
 * **Ported to native 2026-09-11.** Both native apps now implement the same farewell (completion AND
 * dissolve): iOS `TaskFarewellController`/`TaskFarewell.swift`, Android `rememberTaskFarewell`/
 * `TaskFarewell.kt` — both explicitly 1:1 with the transition rules here (`isResolveTransition`/
 * `isDissolveTransition`) and with the same decision-promotion rule this file's snapshot substitution
 * protects (`bucketOfTask`/`awaitsDecision`). Before this, a chip like "Drop it" silently vanished a
 * native row, and neither platform held a completing row for its celebration either — those were two
 * separate native gaps, now closed on all three surfaces.
 */
export function useResolvedTaskFarewell(tasks: WebTask[] | undefined): { burst: boolean; held: HeldResolve[] } {
  // The FULL previous task, not just its status: a dissolve is read off `decisionPrompts`, and the
  // snapshot substituted into the list (below) has to carry whichever field kept the row in its bucket.
  const previous = useRef<Map<string, WebTask> | null>(null);
  const timers = useRef(new Map<string, number[]>());
  const [burst, setBurst] = useState(false);
  const [held, setHeld] = useState<HeldResolve[]>([]);

  useEffect(() => {
    if (!tasks) {
      return;
    }
    const before = previous.current;
    const next = new Map<string, WebTask>();
    for (const task of tasks) {
      if (task.id) {
        next.set(task.id, task);
      }
    }
    previous.current = next;
    if (!before) {
      return;
    }
    // Completion wins over dissolve: a task that answers its last question AND completes in the same
    // read gets the celebration, not the fade.
    const transitions: { task: WebTask; beforeTask: WebTask; kind: ResolveKind }[] = [];
    for (const task of tasks) {
      if (!task.id || timers.current.has(task.id)) continue;
      const beforeTask = before.get(task.id);
      if (isResolveTransition(beforeTask?.status, task.status)) {
        transitions.push({ task, beforeTask: beforeTask!, kind: 'complete' });
      } else if (isDissolveTransition(beforeTask, task)) {
        transitions.push({ task, beforeTask: beforeTask!, kind: 'dissolve' });
      }
    }
    if (transitions.length === 0) {
      return;
    }
    if (transitions.some((t) => t.kind === 'complete')) {
      setBurst(true);
    }
    setHeld((current) => [
      ...current,
      // The snapshot carries the status AND decisionPrompts the row had a moment ago, which is what
      // keeps it in its bucket for the hold — everything else comes from the fresh `task` so the row
      // still shows an updated title/summary if one arrived alongside the transition.
      ...transitions.map(({ task, beforeTask, kind }) => ({
        task: { ...task, status: beforeTask.status, decisionPrompts: beforeTask.decisionPrompts },
        phase: 'hold' as ResolvePhase,
        kind,
      })),
    ]);
    for (const { task, kind } of transitions) {
      const id = task.id!;
      const holdMs = kind === 'complete' ? RESOLVE_HOLD_MS : DISSOLVE_HOLD_MS;
      const exitMs = kind === 'complete' ? RESOLVE_EXIT_MS : DISSOLVE_EXIT_MS;
      const toExit = window.setTimeout(() => setHeld((current) => advanceFarewell(current, id, 'exit')), holdMs);
      const toDrop = window.setTimeout(() => {
        setHeld((current) => advanceFarewell(current, id, 'drop'));
        timers.current.delete(id);
      }, holdMs + exitMs);
      timers.current.set(id, [toExit, toDrop]);
    }
  }, [tasks]);

  // One burst per resolve: cleared once handed out, so the re-render it causes cannot re-arm it.
  useEffect(() => {
    if (!burst) {
      return;
    }
    const timer = window.setTimeout(() => setBurst(false), 1600);
    return () => window.clearTimeout(timer);
  }, [burst]);

  // Unmount only. Deliberately NOT keyed on `held`: this must never run on the hook's own writes.
  const pending = timers.current;
  useEffect(() => () => pending.forEach((entry) => entry.forEach((timer) => window.clearTimeout(timer))), [pending]);

  return { burst, held };
}
